import React from 'react';

import {
    getActivityCurrentSvgUrl,
    getActivityNextSvgUrl,
} from 'flow/components/icons/ActivitySvgs';
import {
    extensionStatuses,
    getActiveUntil,
    getDurationInUserFriendlyFormat,
    getProfileDescriptionAndOperatorNote,
} from 'flow/utils';
import { ActiveCallDirections } from 'flow/utils/call-utils';
import IntervalRenderer from 'flow/components/custom-components/interval-renderer';
import useAppContext from 'flow/hooks/useAppContext';
import moment from 'moment';
import { TypedMap } from 'flow/types/TypedMap';
import { Statuses } from 'flow/types/Extension';
import { twMerge } from 'tailwind-merge';

interface Props {
    profile: TypedMap<any>;
    status: Statuses;
    containerType: 'duo_first' | 'duo_last' | 'single';
    timestamp?: number;
    via?: string;
    activeCallDirection?: string;
    itemRingingIsTicketNonCall?: boolean;
}

const ActivityContainer = ({
    profile,
    status,
    timestamp,
    via,
    containerType,
    activeCallDirection,
    itemRingingIsTicketNonCall,
}: Props) => {
    const { i18next } = useAppContext();

    const statusText = getStatusComponent(
        profile,
        status,
        via,
        activeCallDirection,
        i18next,
        itemRingingIsTicketNonCall,
        containerType === 'single',
    );
    const durationComponent =
        timestamp && status !== 'ringingCall'
            ? getDurationComponent(timestamp, status, containerType, i18next)
            : undefined;

    if (containerType === 'single') {
        return (
            <div
                className={twMerge(
                    'flex h-9 w-fit flex-1 cursor-default items-center gap-x-1 overflow-x-auto rounded-sm px-3 text-sm/none',
                    getSingleItemBackgroundColor(status),
                )}
            >
                {statusText}
                {durationComponent}
            </div>
        );
    }

    return (
        <div
            className={twMerge(
                'flex h-9 w-fit flex-1 cursor-default flex-wrap items-center gap-x-1 px-3 text-sm/none',
                containerType === 'duo_first' ? 'pe-4' : '',
                containerType === 'duo_last' ? '-ms-1 ps-4 pe-3' : '',
            )}
            style={{
                borderImageSource: `url(${getActivitySvgUrl(status, containerType)})`,
                borderImageSlice: '15 fill',
                borderImageWidth: '128px',
            }}
        >
            {statusText}
            {durationComponent}
        </div>
    );
};

export default React.memo(ActivityContainer);

const isInCall = (type: Statuses) =>
    type === extensionStatuses.inCall.key ||
    type === extensionStatuses.ringingCall.key ||
    type === extensionStatuses.inExternalCall.key;

const getDurationComponent = (
    timestamp: number,
    status: Statuses,
    containerType: string,
    i18next: any,
) => (
    <IntervalRenderer interval={1000}>
        {() => {
            // time for phone calls should tick up
            // time for profiles should tick down

            const now = moment();
            const inCall = isInCall(status);
            let timeDuration = moment.duration(moment(timestamp).diff(now));

            if (inCall) {
                timeDuration = moment.duration(now.diff(moment(timestamp)));
            }

            let timeDurationMilliseconds = timeDuration.asMilliseconds();

            if (
                !inCall &&
                timeDurationMilliseconds < Math.abs(Date.now() - timestamp)
            ) {
                // Count down is reached and we don't want it to start count up again
                timeDurationMilliseconds = 0;
            }

            let duration = getDurationInUserFriendlyFormat(
                Math.abs(timeDurationMilliseconds),
            );

            if (containerType === 'duo_last') {
                duration = ` ${i18next.t('in')} ${duration}`;
            }

            return (
                <span
                    className={twMerge(
                        'flex w-fit text-sm/none whitespace-nowrap',
                        containerType === 'single' && status === 'inCall'
                            ? 'font-normal'
                            : '',
                    )}
                >
                    {duration}
                </span>
            );
        }}
    </IntervalRenderer>
);

const getStatusComponent = (
    profile: TypedMap<any>,
    status: Statuses,
    via: string,
    activeCallDirection: string,
    i18next: any,
    itemRingingIsTicketNonCall?: boolean,
    includeProfileUntil?: boolean,
) => {
    return (
        <span className="line-clamp-2 text-sm/none font-bold">
            {textPresentation(
                profile,
                status,
                via,
                activeCallDirection,
                i18next,
                itemRingingIsTicketNonCall,
                includeProfileUntil,
            )}
        </span>
    );
};

const getRingingItemTypei18Key = (
    direction: string,
    isTicketNonCall?: boolean,
) => {
    if (isTicketNonCall) return 'tickets.message.incoming';
    if (direction === ActiveCallDirections.INCOMING.value)
        return 'call.incoming';
    if (direction === ActiveCallDirections.OUTGOING.value)
        return 'call.outgoing';

    //Fallback 'Call', without direction specified. Should probably never end up here
    return 'Supervisor.AgentRow.Tooltip.Label.Call';
};

const textPresentation = (
    profile: TypedMap<any>,
    status: Statuses,
    via: string,
    activeCallDirection: string,
    i18next: any,
    itemRingingIsTicketNonCall?: boolean,
    includeProfileUntil?: boolean,
) => {
    if (status === 'available' || status === 'unavailable') {
        const descriptionAndOperatorNote =
            getProfileDescriptionAndOperatorNote(profile);
        if (!includeProfileUntil || !getActiveUntil(profile, i18next))
            return descriptionAndOperatorNote;

        return (
            <span>
                {descriptionAndOperatorNote}
                <span className="font-normal">
                    {` ${getActiveUntil(profile, i18next)}`}
                </span>
            </span>
        );
    }

    const viaText = i18next.t('general.call.via', {
        queueOrNumber: via,
        interpolation: { escapeValue: false },
    });

    if (status === 'inCall' || status === 'inExternalCall') {
        return via ? viaText : i18next.t('general.call.direct');
    }

    if (status === 'ringingCall') {
        const ringingText = i18next.t(
            getRingingItemTypei18Key(
                activeCallDirection,
                itemRingingIsTicketNonCall,
            ),
        );
        return via
            ? `${ringingText} ${viaText[0].toLowerCase() + viaText.substring(1)}`
            : ringingText;
    }

    return '';
};

// TODO: #team-omni Right now the SVGs are hardcoded to the same color as the tailwind color classes, but they can't be used directly to fill the SVG.
const getActivitySvgUrl = (status: Statuses, containerType: string) => {
    if (containerType === 'duo_first') {
        switch (status) {
            case 'available':
                return getActivityCurrentSvgUrl({ fill: '#2DC659' });
            case 'unavailable':
                return getActivityCurrentSvgUrl({ fill: '#ADADAD' });
            case 'inCall':
                return getActivityCurrentSvgUrl({ fill: '#FD5234' });
            case 'inExternalCall':
                return getActivityCurrentSvgUrl({ fill: '#FD5234' });
            case 'ringingCall':
                return getActivityCurrentSvgUrl({ fill: '#FCD41E' });
            default:
                return '';
        }
    } else if (containerType === 'duo_last') {
        switch (status) {
            case 'available':
                return getActivityNextSvgUrl({ fill: '#2DC659' });
            case 'unavailable':
                return getActivityNextSvgUrl({ fill: '#ADADAD' });
            case 'inCall':
                return getActivityNextSvgUrl({ fill: '#FD5234' });
            case 'inExternalCall':
                return getActivityNextSvgUrl({ fill: '#FD5234' });
            case 'ringingCall':
                return getActivityNextSvgUrl({ fill: '#FCD41E' });
            default:
                return '';
        }
    } else {
        return '';
    }
};

const getSingleItemBackgroundColor = (status: Statuses) => {
    switch (status) {
        case 'available':
            return 'bg-green/20';
        case 'unavailable':
            return 'bg-gray-400/20';
        case 'inCall':
            return 'bg-red/20';
        case 'inExternalCall':
            return 'bg-red/20';
        case 'ringingCall':
            return 'bg-yellow/20';
        default:
            return '';
    }
};
